import { TFunction } from 'i18next';
import * as yup from 'yup';
import { InvoiceScriptEnum } from '../../../common/enums/invoicing.enum';
import { BillingSettingsDto, UpdateBillingSettingsDto } from '../../../common/interfaces/invoicing.interface';
import { bankAccountDigits, formatBankAccount, isValidBankAccount, mod97 } from '../../../utilities/helper/bankAccount.helper';
import { maxText, nameText } from './rules';

/** The form holds text for everything typed, so half-typed input is never coerced away. */
export interface BillingSettingsFormValues {
  legalName: string;
  address: string;
  postalCode: string;
  city: string;
  pib: string;
  mb: string;
  bankAccountNumber: string;
  bankName: string;
  paymentCode: string;
  defaultPaymentDueDays: string;
  isInVatSystem: boolean;
  vatExemptionNote: string;
  vatRegistrationNumber: string;
  invoiceScript: InvoiceScriptEnum;
  contactEmail: string;
  contactPhone: string;
  invoiceGreetingMessage: string;
  invoiceFooterNote: string;
  paymentPurposeTemplate: string;
}

export type BillingSettingsField = keyof BillingSettingsFormValues;

/** The order the form shows them in, which is also the order errors are listed and focused. */
export const BILLING_SETTINGS_FIELDS: BillingSettingsField[] = [
  'legalName',
  'address',
  'postalCode',
  'city',
  'pib',
  'mb',
  'bankAccountNumber',
  'bankName',
  'paymentCode',
  'defaultPaymentDueDays',
  'isInVatSystem',
  'vatExemptionNote',
  'vatRegistrationNumber',
  'invoiceScript',
  'contactEmail',
  'contactPhone',
  'invoiceGreetingMessage',
  'invoiceFooterNote',
  'paymentPurposeTemplate'
];

export const PURPOSE_MAX = 60;
/** What the IPS QR code keeps of the purpose; the rest is cut off. */
export const PURPOSE_QR_MAX = 35;
export const PURPOSE_TOKENS = ['period', 'childName', 'invoiceNumber'] as const;
/** Rendered like the backend's renderPurpose, with a sample invoice. */
export const PURPOSE_SAMPLE: Record<(typeof PURPOSE_TOKENS)[number], string> = {
  period: '10-2026',
  childName: 'Ana Petrović',
  invoiceNumber: 'UG-12/26-01'
};

export const renderPurpose = (template: string): string =>
  (template ?? '').replace(/\{(\w+)\}/g, (match, key: string) => (PURPOSE_SAMPLE as Record<string, string>)[key] ?? match);

export const toFormValues = (settings: BillingSettingsDto | null): BillingSettingsFormValues => ({
  legalName: settings?.legalName ?? '',
  address: settings?.address ?? '',
  postalCode: settings?.postalCode ?? '',
  city: settings?.city ?? '',
  pib: settings?.pib ?? '',
  mb: settings?.mb ?? '',
  bankAccountNumber: settings?.bankAccountNumber ? formatBankAccount(settings.bankAccountNumber) : '',
  bankName: settings?.bankName ?? '',
  paymentCode: settings?.paymentCode ?? '189',
  defaultPaymentDueDays: String(settings?.defaultPaymentDueDays ?? 15),
  isInVatSystem: settings?.isInVatSystem ?? false,
  vatExemptionNote: settings?.vatExemptionNote ?? '',
  vatRegistrationNumber: settings?.vatRegistrationNumber ?? '',
  invoiceScript: settings?.invoiceScript ?? InvoiceScriptEnum.LATIN,
  contactEmail: settings?.contactEmail ?? '',
  contactPhone: settings?.contactPhone ?? '',
  invoiceGreetingMessage: settings?.invoiceGreetingMessage ?? '',
  invoiceFooterNote: settings?.invoiceFooterNote ?? '',
  paymentPurposeTemplate: settings?.paymentPurposeTemplate ?? 'Vrtic {period}'
});

/**
 * What goes to the server.
 *
 * An empty legal name or payment code never gets this far - the schema stops it, as the
 * server would. Optional text goes as '', which the server stores as null.
 */
export const toUpdateDto = (values: BillingSettingsFormValues): UpdateBillingSettingsDto => {
  const text = (value: string) => value.trim();
  return {
    legalName: text(values.legalName),
    address: text(values.address),
    postalCode: text(values.postalCode),
    city: text(values.city),
    pib: bankAccountDigits(values.pib),
    mb: bankAccountDigits(values.mb),
    bankAccountNumber: bankAccountDigits(values.bankAccountNumber) || null,
    bankName: text(values.bankName),
    paymentCode: text(values.paymentCode),
    defaultPaymentDueDays: Number(values.defaultPaymentDueDays),
    isInVatSystem: values.isInVatSystem,
    vatExemptionNote: text(values.vatExemptionNote),
    vatRegistrationNumber: text(values.vatRegistrationNumber),
    invoiceScript: values.invoiceScript,
    contactEmail: text(values.contactEmail),
    contactPhone: text(values.contactPhone),
    invoiceGreetingMessage: values.invoiceGreetingMessage,
    invoiceFooterNote: values.invoiceFooterNote,
    paymentPurposeTemplate: text(values.paymentPurposeTemplate)
  } as UpdateBillingSettingsDto;
};

const digitsField = (t: TFunction, count: number, messageKey: string) =>
  yup
    .string()
    .default('')
    .test('digits', function (raw) {
      const value = (raw ?? '').trim();
      if (!value) return true;
      if (!/^[\d\s-]+$/.test(value)) return this.createError({ message: t('validation.digitsOnly') });
      const now = bankAccountDigits(value).length;
      return now === count || this.createError({ message: t(messageKey, { now }) });
    });

/**
 * Errors: these block saving. Wrong formats, and the two fields the server will not store
 * empty - the legal name and the payment code.
 *
 * An empty address or account is not an error - the server stores those incomplete and
 * refuses at issue time - so they are warnings, from `billingSettingsWarnings`.
 */
export const BLOCKING_WHEN_EMPTY: BillingSettingsField[] = ['legalName', 'paymentCode'];

export const billingSettingsSchema = (t: TFunction) =>
  yup.object({
    // the server refuses an empty name, so it is an error here, not a missing-for-issuing warning
    legalName: nameText(t, 2, 150, t('validation.settings.legalNameEmpty')),
    address: maxText(t, 255),
    postalCode: maxText(t, 10),
    city: maxText(t, 100),
    pib: digitsField(t, 9, 'validation.settings.pibLength'),
    mb: digitsField(t, 8, 'validation.settings.mbLength'),
    bankAccountNumber: yup
      .string()
      .default('')
      .test('account', function (raw) {
        const value = (raw ?? '').trim();
        if (!value) return true;
        if (!/^[\d\s-]+$/.test(value)) return this.createError({ message: t('validation.settings.accountChars') });
        const digits = bankAccountDigits(value);
        if (digits.length !== 18) return this.createError({ message: t('validation.settings.accountLength', { now: digits.length }) });
        return mod97(digits) === 1 || this.createError({ message: t('validation.settings.accountControl') });
      }),
    bankName: maxText(t, 100),
    paymentCode: yup
      .string()
      .default('')
      .test('code', t('validation.settings.paymentCode'), value => /^\d{3}$/.test((value ?? '').trim())),
    defaultPaymentDueDays: yup
      .string()
      .default('')
      .test('days', t('validation.settings.dueDays'), value => {
        const text = (value ?? '').trim();
        return /^\d+$/.test(text) && Number(text) <= 365;
      }),
    isInVatSystem: yup.boolean().default(false),
    vatExemptionNote: maxText(t, 255),
    vatRegistrationNumber: maxText(t, 20),
    invoiceScript: yup.mixed<InvoiceScriptEnum>().oneOf(Object.values(InvoiceScriptEnum)),
    contactEmail: maxText(t, 150).test(
      'email',
      t('validation.settings.email'),
      value => !(value ?? '').trim() || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((value ?? '').trim())
    ),
    contactPhone: maxText(t, 50),
    invoiceGreetingMessage: yup
      .string()
      .default('')
      .test('max', function (value) {
        const now = (value ?? '').length;
        return now <= 2000 || this.createError({ message: t('validation.maxLength', { max: 2000, now }) });
      }),
    invoiceFooterNote: yup
      .string()
      .default('')
      .test('max', function (value) {
        const now = (value ?? '').length;
        return now <= 2000 || this.createError({ message: t('validation.maxLength', { max: 2000, now }) });
      }),
    paymentPurposeTemplate: yup
      .string()
      .default('')
      .test('purpose', function (raw) {
        const value = (raw ?? '').trim();
        if (!value) return this.createError({ message: t('validation.settings.purposeEmpty') });
        if (value.length > PURPOSE_MAX) return this.createError({ message: t('validation.settings.purposeMax', { max: PURPOSE_MAX, now: value.length }) });
        const unknown = (value.match(/\{[^}]*\}/g) ?? []).find(token => !(PURPOSE_TOKENS as readonly string[]).includes(token.slice(1, -1)));
        return !unknown || this.createError({ message: t('validation.settings.purposeToken', { token: unknown }) });
      })
  });

/** What an invoice cannot be issued without, as the server's `missingForInvoicing` judges it. */
export const MISSING_FIELDS: BillingSettingsField[] = ['legalName', 'address', 'bankAccountNumber', 'paymentCode'];

/** The empty fields the invoice needs and the server still stores. Orange; saving is allowed. */
export const billingSettingsWarnings = (t: TFunction, values: BillingSettingsFormValues): Partial<Record<BillingSettingsField, string>> => {
  const warnings: Partial<Record<BillingSettingsField, string>> = {};
  if (!values.address.trim()) warnings.address = t('validation.settings.addressMissing');
  if (!values.bankAccountNumber.trim()) warnings.bankAccountNumber = t('validation.settings.accountMissing');
  return warnings;
};

export const accountIsValid = (value: string) => isValidBankAccount(value);
